import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useNavigate, useParams } from "react-router";
import { TextField, RadioGroup, Radio, FormControlLabel, Button, FormLabel } from '@mui/material';
import config from '../config';

const CreateExercice = () => {
    const { user } = useContext(UserContext);
    const { id_cours, id_lesson, id_exercice } = useParams();
    const [isEditing, setIsEditing] = useState(!!id_exercice);

    const [titre, setTitre] = useState('');
    const [questions, setQuestions] = useState([{
        enonce: '',
        type: 'qcm',
        reponses_correctes: [{ label: '', image_url: '', est_correcte: true }],
        reponses_incorrectes: [{ label: '', image_url: '', est_correcte: false }],
        image_url: ''
    }]);
    const [error, setError] = useState(null);
    const [formError, setFormError] = useState(null);
    const navigate = useNavigate();

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };


    useEffect(() => {
        document.title = "Ajout d'exercice";
        if (!user) {
            navigate('/connexion'); // Redirect to login if user is not logged in
            return;
        }

        const fetchLessonDetails = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${config.apiBaseUrl}/lessons/${id_lesson}?withCourseDetails=true`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch lesson details');
                }
                const lesson = await response.json();
                if (lesson.id_formateur !== user.id_utilisateur && user.type !== 'admin') {
                    setError('Vous n\'êtes pas autorisé à ajouter un exercice à cette leçon.');
                }
            } catch (err) {
                console.error(err);
                setError('Erreur lors de la vérification des autorisations.');
            }
        };

        fetchLessonDetails();
    }, [user, id_lesson, navigate]);

    useEffect(() => {
        if (isEditing) {
            document.title = "Modification d'exercice";
            const fetchExerciceDetails = async () => {
                const token = localStorage.getItem('token');
                try {
                    const response = await fetch(`${config.apiBaseUrl}/exercices/${id_exercice}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (!response.ok) {
                        throw new Error('Échec du chargement des détails de l\'exercice');
                    }
                    const exercice = await response.json();
                    setTitre(exercice.titre);
                    console.log("exo questions", exercice.questions)
                    setQuestions(exercice.questions.map(q => ({
                        id_question: q.id_question,
                        enonce: q.enonce,
                        type: q.type,
                        reponses_correctes: q.reponses?.filter(r => r.est_correcte) || [],
                        reponses_incorrectes: q.reponses?.filter(r => !r.est_correcte) || [],
                        image_url: q.image_url || ''
                    })));
                } catch (err) {
                    console.error(err);
                    setError('Erreur lors du chargement de l\'exercice.');
                }
            };
            fetchExerciceDetails();
        }
    }, [isEditing, id_exercice]);


    const handleQuestionChange = (index, field, value) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
        setQuestions(updatedQuestions);
    };


    const handleAnswerChange = (questionIndex, answerType, answerIndex, value, changeType = "label") => {
        const updatedQuestions = [...questions];
        if (changeType === "label") {
            updatedQuestions[questionIndex][answerType][answerIndex].label = value;
        } else {
            updatedQuestions[questionIndex][answerType][answerIndex].image_url = value;
        }

        console.log("updated", updatedQuestions[questionIndex][answerType][answerIndex])
        setQuestions(updatedQuestions);
    };



    const handleAddAnswer = (questionIndex, answerType) => {
        const updatedQuestions = [...questions];
        updatedQuestions[questionIndex][answerType].push({ label: "", image_url: '', est_correcte: answerType == "reponses_correctes" });
        setQuestions(updatedQuestions);
    };

    const handleRemoveAnswer = (questionIndex, answerType, answerIndex) => {
        const updatedQuestions = [...questions];
        updatedQuestions[questionIndex][answerType].splice(answerIndex, 1);
        setQuestions(updatedQuestions);
    };


    const handleAddQuestion = () => {
        setQuestions([...questions, {
            enonce: '',
            type: 'qcm',
            reponses_correctes: [{ label: '', image_url: '', est_correcte: true }],
            reponses_incorrectes: [{ label: '', image_url: '', est_correcte: false }],
            image_url: "",
        }]);
    };

    const handleRemoveQuestion = (index) => {
        const question = questions[index];

        // Vérifier si la question contient des données
        const hasContent = question.enonce.trim() !== '' ||
            question.image_url.trim() !== '' ||
            question.reponses_correctes.some(ans => ans.label.trim() !== '' || ans.image_url.trim() !== "") ||
            question.reponses_incorrectes.some(ans => ans.label.trim() !== '' || ans.image_url.trim() !== "");

        if (hasContent) {
            const confirmDelete = window.confirm("Cette question contient des données. Voulez-vous vraiment la supprimer ?");
            if (!confirmDelete) return;
        }

        // Supprimer la question si confirmée ou vide
        const updatedQuestions = [...questions];
        updatedQuestions.splice(index, 1);
        setQuestions(updatedQuestions);
    };

    const setErrorWithScroll = (message) => {
        setFormError(message);
        window.scrollTo(0, 0); // Scroll to top on error
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validate and format questions before sending to the backend
        const formattedQuestions = [];

        for (const question of questions) {
            const { enonce, type, reponses_correctes, reponses_incorrectes } = question;

            // Filter out empty answers
            const filteredCorrectAnswers = reponses_correctes.filter(answer => answer.label.trim() !== '');
            const filteredIncorrectAnswers = reponses_incorrectes.filter(answer => answer.label.trim() !== '');

            if (question.image_url.trim() !== '' && !isValidUrl(question.image_url)) {
                setErrorWithScroll(`L'URL de l'image fournie pour la question : "${enonce}" n'est pas valide.`);
                return;
            }

            // Validation: Check for empty correct answers
            if (filteredCorrectAnswers.length === 0) {
                setErrorWithScroll(`Toutes les réponses correctes sont vides pour la question : "${enonce}". Veuillez fournir au moins une réponse correcte.`);
                return;
            }

            // Validation: Check for duplicates in correct answers
            const correctLabels = filteredCorrectAnswers.map(a => a.label.trim());
            const uniqueCorrectLabels = new Set(correctLabels);
            if (uniqueCorrectLabels.size !== correctLabels.length) {
                setErrorWithScroll(`Des réponses correctes en double ont été trouvées pour la question : "${enonce}". Veuillez supprimer les doublons.`);
                return;
            }



            // Validation for qcm type questions
            var reponses_possibles = null;
            if (type === 'qcm') {
                // Check if all incorrect answers are empty
                if (filteredIncorrectAnswers.length === 0) {
                    setErrorWithScroll(`Toutes les réponses incorrectes sont vides pour la question : "${enonce}". Veuillez fournir au moins une réponse incorrecte.`);
                    return;
                }

                // Check for duplicates in incorrect answers
                const incorrectLabels = filteredIncorrectAnswers.map(a => a.label.trim());
                const uniqueIncorrectLabels = new Set(incorrectLabels);
                if (uniqueIncorrectLabels.size !== incorrectLabels.length) {
                    setErrorWithScroll(`Des réponses incorrectes en double ont été trouvées pour la question : "${enonce}". Veuillez supprimer les doublons.`);
                    return;
                }


                // Check for duplicates between correct and incorrect answers
                for (const label of correctLabels) {
                    if (uniqueIncorrectLabels.has(label)) {
                        setErrorWithScroll(`La réponse "${label}" apparaît à la fois dans les réponses correctes et incorrectes pour la question : "${enonce}". Veuillez corriger cela.`);
                        return;
                    }
                }



                // Ensure that possible answers include correct and incorrect ones, then shuffle
                reponses_possibles = [...filteredCorrectAnswers, ...filteredIncorrectAnswers];
                reponses_possibles = reponses_possibles.sort(() => Math.random() - 0.5);
                console.log("reponses", reponses_possibles)

            }

            // Prepare the formatted question object
            formattedQuestions.push({
                id_question: question.id_question || undefined,
                enonce: enonce,
                type: type,
                reponses: reponses_possibles || [...filteredCorrectAnswers],
                image_url: question.image_url.trim() !== '' ? question.image_url : null
            });

        }

        const requestData = {
            titre,
            questions: formattedQuestions
        };

        console.log("request", requestData)
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(
                isEditing ? `${config.apiBaseUrl}/exercices/${id_exercice}` : `${config.apiBaseUrl}/exercices/${id_lesson}`,
                {
                    method: isEditing ? 'PUT' : 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestData)
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Échec de la ${isEditing ? "modification" : "création"} de l'exercice`);
            }

            alert(`Exercice ${isEditing ? "modifié" : "créé"} avec succès`);
            navigate(`/cours/${id_cours}/lecon/${id_lesson}`);

        } catch (error) {
            console.error(`Erreur lors de la ${isEditing ? "modification" : "création"} de l'exercice:`, error);
            setErrorWithScroll(error.message);
        }
    };

    if (error) {
        return <div className="alert alert-danger" role="alert">{error}</div>;
    }

    //console.log("questions", questions)

    return (
        <div>
            {formError && <div className="alert alert-danger" role="alert">{formError}</div>}
            <div className="container my-5">
                <h1 className="mb-4">Créer un exercice</h1>
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <TextField
                            label="Titre de l'exercice"
                            fullWidth
                            value={titre}
                            onChange={(e) => setTitre(e.target.value)}
                            required
                        />
                    </div>

                    {questions.map((question, index) => (
                        <div key={index} className="mb-4">
                            <h2>Question {index + 1}</h2>
                            <div className="mb-3">
                                <TextField
                                    label="Enoncé de la question"
                                    fullWidth
                                    value={question.enonce}
                                    onChange={(e) => handleQuestionChange(index, 'enonce', e.target.value)}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <TextField
                                    label="Image (Url, optionnel)"
                                    fullWidth
                                    value={question.image_url}
                                    onChange={(e) => handleQuestionChange(index, 'image_url', e.target.value)}
                                />
                            </div>

                            {question.image_url.trim() !== '' && isValidUrl(question.image_url) && (
                                <img src={question.image_url} alt="" style={{ maxWidth: '100%', marginTop: '10px' }} />
                            )}



                            <div className="mb-3">
                                <FormLabel>Type de question</FormLabel>
                                <RadioGroup
                                    value={question.type}
                                    onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
                                >
                                    <FormControlLabel value="qcm" control={<Radio />} label="QCM" />
                                    <FormControlLabel value="question ouverte" control={<Radio />} label="Question ouverte" />
                                </RadioGroup>
                            </div>

                            <div className="mb-3">
                                <h3>Réponses correctes</h3>
                                {question.reponses_correctes.map((answer, i) => (
                                    <div key={i} className="mb-3">
                                        <TextField
                                            label={`Réponse correcte ${i + 1}`}
                                            fullWidth
                                            value={answer.label}
                                            onChange={(e) => handleAnswerChange(index, 'reponses_correctes', i, e.target.value)}
                                            required
                                        />

                                        {question.type === 'qcm' && <TextField
                                            label={`Image Réponse correcte ${i + 1} (Url, optionnel)`}
                                            fullWidth
                                            value={answer.image_url}
                                            onChange={(e) => handleAnswerChange(index, 'reponses_correctes', i, e.target.value, "img")}
                                        />}

                                        {question.type === 'qcm' && answer.image_url && answer.image_url.trim() !== '' && isValidUrl(answer.image_url) && (
                                            <p><img src={answer.image_url} alt="" style={{ maxWidth: '100%', marginTop: '10px' }} /></p>
                                        )}
                                        {question.reponses_correctes.length > 1 && (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => handleRemoveAnswer(index, 'reponses_correctes', i)}
                                                className="mt-2"
                                            >
                                                Supprimer cette réponse
                                            </Button>
                                        )}
                                    </div>
                                ))}
                                <Button variant="outlined" onClick={() => handleAddAnswer(index, 'reponses_correctes')}>
                                    Ajouter une réponse correcte
                                </Button>
                            </div>

                            {question.type === 'qcm' && (
                                <div className="mb-3">
                                    <h3>Réponses incorrectes</h3>
                                    {question.reponses_incorrectes.map((answer, i) => (
                                        <div key={i} className="mb-3">
                                            <TextField
                                                label={`Réponse incorrecte ${i + 1}`}
                                                fullWidth
                                                value={answer.label}
                                                onChange={(e) => handleAnswerChange(index, 'reponses_incorrectes', i, e.target.value)}
                                                required
                                            />

                                            <TextField
                                                label={`Image Réponse correcte ${i + 1} (Url, optionnel)`}
                                                fullWidth
                                                value={answer.image_url}
                                                onChange={(e) => handleAnswerChange(index, 'reponses_incorrectes', i, e.target.value, "img")}
                                            />

                                            {answer.image_url && answer.image_url.trim() !== '' && isValidUrl(answer.image_url) && (
                                                <p><img src={answer.image_url} alt="" style={{ maxWidth: '100%', marginTop: '10px' }} /></p>
                                            )}
                                            {question.reponses_incorrectes.length > 1 && (
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={() => handleRemoveAnswer(index, 'reponses_incorrectes', i)}
                                                    className="mt-2"
                                                >
                                                    Supprimer cette réponse
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button variant="outlined" onClick={() => handleAddAnswer(index, 'reponses_incorrectes')}>
                                        Ajouter une réponse incorrecte
                                    </Button>
                                </div>
                            )}


                            {questions.length > 1 && <Button variant="contained" color="secondary" onClick={() => handleRemoveQuestion(index)}>
                                Supprimer la question
                            </Button>}
                        </div>
                    ))}

                    <Button variant="outlined" onClick={handleAddQuestion}>
                        Ajouter une question
                    </Button>

                    <div className="mt-4">
                        <Button type="submit" variant="contained" color="primary">
                            {isEditing ? "Modifier l'exercice" : "Créer l'exercice"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateExercice;